import { validateBarcode } from "../eanValidator";
import type { ParsedPriceTag, ParsedPriceTagSize, ParsePriceTagOptions, PriceTagSizeUnit } from "./types";

const SUPER_DIGITS: Record<string, string> = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
};

const LOYALTY_RE = /\b(ait[aä]h|kaardiga|depo\s*kaardiga|club|bonus|koos\s*kaardiga)\b/i;
const PROMO_RE = /(?:-\s*\d+\s*%|super[- ]?hind|allahindlus|soodushind|kampaan)/i;
const REGULAR_RE = /\b(tavahind|ilma\s*(depo\s*)?kaardita|without\s*card)\b/i;
const BULK_RE = /\b(mahuhind|ja\s*rohkem|tk\s*ostmisel|\d+\s*tk\s+ja)\b/i;
const SKIP_LINE_RE =
  /^(periood|p[aä]ritolu|vt\.?|pakendilt|pant|1\s*kg\s*=|eur\s*\/|€\s*\/)/i;

const KNOWN_BRANDS = [
  "finish",
  "vanish",
  "woolite",
  "nescaf",
  "nescafe",
  "lavazza",
  "jacobs",
  "l'or",
  "lay's",
  "lays",
  "valio",
  "rakvere",
  "ranna",
  "freshona",
  "baresa",
  "loyd",
  "energizer",
  "camelion",
  "fentimans",
  "sanpellegrino",
  "san pellegrino",
];

function replaceSuperscripts(text: string): string {
  return text.replace(/(\d)([⁰¹²³⁴⁵⁶⁷⁸⁹]+)/g, (_, whole: string, frac: string) => {
    const cents = [...frac].map((ch) => SUPER_DIGITS[ch] ?? "").join("");
    return `${whole}.${cents}`;
  });
}

function normalizeSpaces(text: string): string {
  return text
    .replace(/\u00a0/g, " ")
    .replace(/[|]/g, " ")
    .replace(/[€]/g, " € ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseNumber(raw: string): number | null {
  const cleaned = raw.replace(/\s+/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

function spacedPrice(line: string): number | null {
  const match = line.match(/\b(\d{1,3})\s+(\d{2})\b/);
  if (!match) return null;
  return parseNumber(`${match[1]}.${match[2]}`);
}

function extractPrices(line: string): number[] {
  const values: number[] = [];
  const spaced = spacedPrice(line);
  if (spaced !== null && spaced >= 0.15 && spaced < 500) values.push(spaced);

  const re = /(\d{1,3}[.,]\d{2})/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(line))) {
    const value = parseNumber(match[1]);
    if (value !== null && value >= 0.15 && value < 500) values.push(value);
  }
  return [...new Set(values)];
}

function isUnitPriceLine(line: string): boolean {
  return /(?:€|eur)?\s*\/\s*(kg|l|tk|pcs)\b/i.test(line) || /1\s*kg\s*=/i.test(line);
}

function parseUnitPrice(line: string): ParsedPriceTag["unitPrice"] | undefined {
  const kg = line.match(/(\d+[.,]\d{2})\s*(?:€|eur)?\s*\/\s*kg/i) || line.match(/1\s*kg\s*=\s*(\d+[.,]\d{2})/i);
  if (kg) {
    const value = parseNumber(kg[1]);
    if (value !== null) return { value, unit: "kg" };
  }
  const liter = line.match(/(\d+[.,]\d{2})\s*(?:€|eur)?\s*\/\s*l\b/i);
  if (liter) {
    const value = parseNumber(liter[1]);
    if (value !== null) return { value, unit: "l" };
  }
  const pcs = line.match(/(\d+[.,]\d{2})\s*(?:€|eur)?\s*\/\s*(?:tk|pcs)\b/i);
  if (pcs) {
    const value = parseNumber(pcs[1]);
    if (value !== null) return { value, unit: "pcs" };
  }
  return undefined;
}

function parseSize(text: string): ParsedPriceTagSize | undefined {
  const pack = text.match(/(\d+)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(g|kg|ml|l|cl)\b/i);
  if (pack) {
    const count = Number(pack[1]);
    const each = parseNumber(pack[2].replace(",", ".")) ?? Number(pack[2]);
    const unit = normalizeUnit(pack[3]);
    if (count > 0 && each > 0 && unit) {
      return { value: count * each, unit };
    }
  }
  const simple = text.match(/(\d+(?:[.,]\d+)?)\s*(kg|g|ml|l|cl)\b/i);
  if (simple) {
    const value = parseNumber(simple[1].replace(",", ".")) ?? Number(simple[1]);
    const unit = normalizeUnit(simple[2]);
    if (value > 0 && unit) return { value, unit };
  }
  const pcs = text.match(/(\d+)\s*(tk|pcs)\b/i);
  if (pcs) {
    const value = Number(pcs[1]);
    if (value > 0) return { value, unit: "pcs" };
  }
  return undefined;
}

function normalizeUnit(raw: string): PriceTagSizeUnit | undefined {
  const unit = raw.toLowerCase();
  if (unit === "g" || unit === "kg" || unit === "ml" || unit === "l") return unit;
  if (unit === "cl") return "ml";
  if (unit === "tk" || unit === "pcs") return "pcs";
  return undefined;
}

function looksLikeEan(value: string): boolean {
  return validateBarcode(value).isValid;
}

function extractCodes(text: string): { barcode?: string; internalProductCode?: string } {
  const spaced = (text.match(/\d(?:[\s-]?\d){7,13}/g) ?? [])
    .map((match) => match.replace(/[\s-]/g, ""))
    .filter((value) => value.length >= 8 && value.length <= 14);
  const compact = text.replace(/[^\d\n]/g, " ");
  const numbers = [...new Set([...spaced, ...(compact.match(/\d{6,14}/g) ?? [])])];
  const ranked = [...numbers].sort((a, b) => {
    const score = (value: string) => (looksLikeEan(value) ? 10 : 0) + value.length;
    return score(b) - score(a);
  });
  const barcode = ranked.find((raw) => raw.length === 13 || raw.length === 12 || raw.length === 8);
  const internalProductCode = ranked.find((raw) => raw !== barcode && raw.length >= 6 && raw.length <= 10);
  return { barcode, internalProductCode };
}

function detectLoyaltyProgram(text: string, storeId?: string): string | undefined {
  if (/ait[aä]h/i.test(text)) return "Aitäh";
  if (/depo/i.test(text) || storeId === "depo") return "DEPO";
  if (LOYALTY_RE.test(text)) return "loyalty";
  return undefined;
}

function guessBrand(lines: string[], productName?: string): string | undefined {
  const haystack = `${lines[0] ?? ""} ${productName ?? ""}`.toLowerCase();
  for (const brand of KNOWN_BRANDS) {
    if (haystack.includes(brand.trim().toLowerCase())) {
      return brand.trim().replace(/\b\w/g, (ch) => ch.toUpperCase());
    }
  }
  const first = (lines[0] ?? "").trim();
  if (first && first === first.toUpperCase() && first.length <= 24 && !/\d{2,}/.test(first)) {
    return first.replace(/\s+/g, " ");
  }
  return undefined;
}

function classifyStore(text: string, storeId?: string): "coop" | "depo" | "generic" {
  if (storeId === "depo" || /\bdepo\b/i.test(text) || /mahuhind/i.test(text)) return "depo";
  if (storeId === "coop" || /ait[aä]h/i.test(text)) return "coop";
  return "generic";
}

/**
 * Turns raw OCR text (plus optional machine-readable codes) into structured
 * price-tag fields. Store-specific hints only change price-role assignment.
 */
export function parsePriceTag(rawText: string, options: ParsePriceTagOptions = {}): ParsedPriceTag {
  const original = rawText ?? "";
  const prepared = replaceSuperscripts(original);
  const lines = prepared
    .split(/\n+/)
    .map((line) => normalizeSpaces(line))
    .filter(Boolean);

  const joined = lines.join("\n");
  const family = classifyStore(joined, options.storeId);
  const codes = extractCodes(joined);
  const size = parseSize(joined);

  const productPrices: number[] = [];
  let unitPrice: ParsedPriceTag["unitPrice"];
  let sawLoyalty = false;
  let sawPromo = false;
  let sawRegular = false;
  let sawBulk = false;

  for (const line of lines) {
    if (isUnitPriceLine(line)) {
      unitPrice = unitPrice ?? parseUnitPrice(line);
      continue;
    }
    if (LOYALTY_RE.test(line)) sawLoyalty = true;
    if (PROMO_RE.test(line)) sawPromo = true;
    if (REGULAR_RE.test(line)) sawRegular = true;
    if (BULK_RE.test(line)) sawBulk = true;
    for (const price of extractPrices(line)) {
      if (unitPrice && Math.abs(price - unitPrice.value) < 0.001) continue;
      productPrices.push(price);
    }
  }

  const uniquePrices = [...new Set(productPrices)].sort((a, b) => b - a);
  const tag: ParsedPriceTag = {
    rawText: original.trim(),
    size,
    unitPrice,
    barcode: options.detectedBarcode || codes.barcode,
    internalProductCode: options.detectedInternalCode || codes.internalProductCode,
    loyaltyProgram: detectLoyaltyProgram(joined, options.storeId),
  };

  if (uniquePrices.length === 1) {
    tag.regularPrice = uniquePrices[0];
  } else if (uniquePrices.length >= 2) {
    const high = uniquePrices[0];
    const low = uniquePrices[uniquePrices.length - 1];
    if (family === "depo" || sawBulk) {
      tag.regularPrice = high;
      tag.loyaltyPrice = uniquePrices[1] ?? low;
      tag.promoPrice = uniquePrices.length > 2 ? low : undefined;
    } else if (family === "coop" || sawLoyalty || sawRegular) {
      tag.regularPrice = high;
      tag.loyaltyPrice = low;
    } else if (sawPromo) {
      tag.regularPrice = high;
      tag.promoPrice = low;
    } else {
      tag.regularPrice = high;
      tag.promoPrice = low;
    }
  }

  const nameLines = lines.filter((line) => {
    if (SKIP_LINE_RE.test(line)) return false;
    if (isUnitPriceLine(line)) return false;
    if (extractPrices(line).length > 0 && line.length < 18) return false;
    if (/^\d{6,}$/.test(line.replace(/\s/g, ""))) return false;
    if (LOYALTY_RE.test(line) && line.length < 24) return false;
    return /[a-zäöõüšžA-ZÄÖÕÜŠŽ]/.test(line);
  });

  if (nameLines.length > 0) {
    tag.productName = nameLines.slice(0, 2).join(" ").replace(/\s+/g, " ").trim();
    tag.brand = guessBrand(nameLines, tag.productName);
  }

  return tag;
}
